import type { Content, TableCell } from 'pdfmake/interfaces';
import type { ViewBlock } from '@/data/schemas';
import { cm, pdfColors } from '../../theme.pdf';
import { BLOCK_SPACING } from '../layout';

type ImageBlock = Extract<ViewBlock, { type: 'image' }>;

const DEFAULT_MAX_WIDTH_CM = 3.2;
const DEFAULT_MAX_HEIGHT_CM = 2.2;
const FRAME_PADDING_CM = 0.1;

function canRenderImage(src?: string): src is string {
  if (!src) return false;
  return src.startsWith('data:') || src.startsWith('blob:');
}

export function renderImageBlock(block: ImageBlock): Content {
  const maxWidth = cm(block.maxWidthCm ?? DEFAULT_MAX_WIDTH_CM);
  const maxHeight = cm(block.maxHeightCm ?? DEFAULT_MAX_HEIGHT_CM);
  const alignment = block.align ?? 'right';
  const fallbackText = block.fallbackText ?? 'Logo non disponibile';
  const framePadding = cm(FRAME_PADDING_CM);
  const fitWidth = Math.max(1, maxWidth - framePadding * 2);
  const fitHeight = Math.max(1, maxHeight - framePadding * 2);

  const createFramedCell = (cell: TableCell): Content => ({
    table: {
      widths: [maxWidth],
      heights: [maxHeight],
      body: [[cell]],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => pdfColors.borderGray,
      vLineColor: () => pdfColors.borderGray,
      paddingLeft: () => framePadding,
      paddingRight: () => framePadding,
      paddingTop: () => framePadding,
      paddingBottom: () => framePadding,
    },
    alignment,
    margin: [0, 0, 0, BLOCK_SPACING],
  });

  if (!canRenderImage(block.src)) {
    const placeholderCell = {
      text: fallbackText,
      style: 'bodyMuted',
      alignment: 'center',
      valign: 'middle',
      fillColor: pdfColors.lightGray,
    } as unknown as TableCell;

    return createFramedCell(placeholderCell);
  }

  const imageCell = {
    image: block.src,
    fit: [fitWidth, fitHeight],
    alignment: 'center',
    valign: 'middle',
  } as unknown as TableCell;

  return createFramedCell(imageCell);
}
